import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowLeftIcon } from "lucide-react"
import { useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

import BrandLogo from "@/components/commons/app-brand-logo"
import { LoaderButton } from "@/components/commons/loader-button"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { FRONTEND_ROUTES } from "@/constants"
import { useAuth } from "@/contexts/auth"
import { useWhitelabel } from "@/contexts/whitelabel"
import { toast } from "@/hooks/use-toast"
import type { ForgotPasswordFormSchemaProps } from "@/schemas/forgot-form"
import { forgotPasswordFormSchema } from "@/schemas/forgot-form"
import { forgotPassword } from "@/services"

export const ForgotPasswordForm = () => {
  const { t } = useTranslation()
  const { handleSetForgotData } = useAuth()
  const { configuration } = useWhitelabel()
  const navigate = useNavigate()
  const brand = import.meta.env.VITE_BRAND ?? "pay2b"
  const schema = useMemo(
    () => forgotPasswordFormSchema(t),
    [t]
  )
  const form = useForm<ForgotPasswordFormSchemaProps>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
    },
  })
  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await forgotPassword(values.email)
      toast({
        title: t("auth.emailSentSuccess"),
        variant: "success",
      })
      handleSetForgotData(values.email)
      navigate(FRONTEND_ROUTES.RECOVERY_PASSWORD.href)
    } catch {
      toast({
        title: t("auth.emailSentError"),
        variant: "destructive",
      })
    }
  })
  const isPending = form.formState.isSubmitting
  return (
    <div className="w-full max-w-[400px] space-y-8">
      {/* Logo e Título */}
      <div className="flex flex-col items-center space-y-6">
        <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl p-3">
          <div
            className="absolute inset-0 rounded-2xl"
            style={{
              backgroundColor: "var(--whitelabel-primary)",
              opacity: 0.1,
            }}
          />
          <BrandLogo
            brand={brand}
            className="relative z-10 h-10 w-10"
            customLogo={configuration?.merchant?.logo}
            customAlt={configuration?.merchant?.fantasyName}
          />
        </div>
        <div className="space-y-2 text-center">
          <h1 className="page-title tracking-tight text-foreground">
            {t("auth.forgotPassword")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("auth.forgotPasswordDescription")}
          </p>
        </div>
      </div>
      {/* Formulário */}
      <div className="flex flex-col rounded-2xl border border-border/50 bg-card p-8 shadow-lg shadow-black/5">
        <Form {...form}>
          <form
            method="POST"
            onSubmit={handleSubmit}
            className="space-y-5"
          >
            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-foreground">
                    {t("auth.email")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder={t(
                        "auth.emailPlaceholder"
                      )}
                      disabled={isPending}
                      className="h-12 rounded-xl border-border/50 bg-background px-4 transition-all focus:border-primary focus:ring-2 focus:ring-primary/20"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex flex-col gap-3 pt-2">
              <LoaderButton
                type="submit"
                isLoading={isPending}
                className="btn-whitelabel h-12 w-full rounded-xl text-base font-medium shadow-lg transition-all hover:shadow-xl"
              >
                {t("auth.confirm")}
              </LoaderButton>
              <Button
                asChild
                variant="outline"
                disabled={isPending}
                className="h-12 w-full rounded-xl border-border/50 transition-all hover:bg-muted"
              >
                <Link to={FRONTEND_ROUTES.LOGIN.href}>
                  <ArrowLeftIcon className="mr-2 h-4 w-4" />
                  {t("common.back")}
                </Link>
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
